import React, { useState } from "react";
import { api } from "@/lib/api";
import { AVATARS, colorOf } from "@/lib/constants";
import { MEMBER_COLORS } from "@/lib/memberColors";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

// Default emoji per role. When someone flips a member's role in Setup,
// we auto-swap the avatar iff they haven't already personalised it —
// meaning the current emoji still matches ANOTHER role's default. This
// keeps the flow feeling smart ("I picked Kid, the icon changed to a
// kid") without ever clobbering a custom choice someone typed in.
const ROLE_DEFAULT_EMOJI = {
  parent: "🙂",
  kid: "🧒",
  pet: "🐕",
  grandparent: "👵",
  other: "🌱",
};
const ROLE_DEFAULT_EMOJIS = new Set(Object.values(ROLE_DEFAULT_EMOJI));

export default function Setup({ onDone }) {
  const { t } = useI18n();
  const [members, setMembers] = useState([
    { name: "", avatar_emoji: ROLE_DEFAULT_EMOJI.parent, color: "sage", role: "parent", birthday: "" },
  ]);
  const [saving, setSaving] = useState(false);

  const update = (i, patch) =>
    setMembers((m) =>
      m.map((x, idx) => {
        if (idx !== i) return x;
        // Role change: auto-swap the avatar to the new role's default,
        // but only if the current emoji is still one of our defaults
        // (i.e. the user hasn't personalised it yet). Preserves any
        // typed-in emoji (😺, 🦔, custom) untouched.
        if (patch.role && patch.role !== x.role) {
          const next = { ...x, ...patch };
          if (ROLE_DEFAULT_EMOJIS.has(x.avatar_emoji)) {
            next.avatar_emoji = ROLE_DEFAULT_EMOJI[patch.role] || x.avatar_emoji;
          }
          return next;
        }
        return { ...x, ...patch };
      })
    );

  const addRow = () =>
    setMembers((m) => {
      if (m.length >= 15) return m;
      const nextColor = MEMBER_COLORS[m.length % MEMBER_COLORS.length].key;
      const nextRole = m.length >= 2 ? "kid" : "parent";
      return [
        ...m,
        {
          name: "",
          avatar_emoji: ROLE_DEFAULT_EMOJI[nextRole] || "🙂",
          color: nextColor,
          role: nextRole,
          birthday: "",
        },
      ];
    });

  const removeRow = (i) => setMembers((m) => m.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    const filled = members.filter((m) => m.name.trim());
    if (filled.length === 0) {
      toast.error(t({ en: "Add at least one family member", da: "Tilføj mindst ét familiemedlem" }));
      return;
    }
    if (!filled.some((m) => m.role === "parent")) {
      toast.error(t({ en: "At least one parent is required", da: "Mindst én forælder er påkrævet" }));
      return;
    }
    // Birthdays drive the shared birthday banner, age-aware features
    // (Kid/Toddler mode) and calendar surfacing, so the backend requires
    // them on everyone who isn't a pet. Catch it here so the user sees
    // a specific, fixable message instead of a generic "Something went
    // wrong".
    const missingBirthday = filled.find(
      (m) => m.role !== "pet" && !m.birthday
    );
    if (missingBirthday) {
      toast.error(
        t({
          en: `Add a birthday for ${missingBirthday.name.trim() || "everyone except pets"} — it's required.`,
          da: `Tilføj en fødselsdag for ${missingBirthday.name.trim() || "alle undtagen kæledyr"} — det er påkrævet.`,
        })
      );
      return;
    }
    setSaving(true);
    try {
      for (const m of filled) {
        await api.createMember({
          name: m.name.trim(),
          avatar_emoji: m.avatar_emoji,
          color: m.color,
          accent_color: m.color,
          role: m.role || "parent",
          birthday: m.birthday || null,
        });
      }
      await onDone();
      toast.success(t({ en: "Welcome to FamFeel", da: "Velkommen til FamFeel" }));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t({ en: "Something went wrong", da: "Noget gik galt" }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="min-h-screen px-6 py-12 md:py-20"
      data-testid="setup-page"
    >
      <div className="max-w-2xl mx-auto">
        <div className="mb-10">
          <div className="label-tiny mb-3">{t({ en: "Welcome · FamFeel", da: "Velkommen · FamFeel" })}</div>
          <h1 className="font-display-italic text-5xl md:text-7xl leading-[1.05] mb-3">
            {t({ en: "Who’s in your family?", da: "Hvem er med i familien?" })}
          </h1>
          <p className="text-lg text-[color:var(--ink-2)]">
            {t({
              en: "Add everyone who’ll check in. No accounts, no passwords — just tap a name. You can add more or set PINs later.",
              da: "Tilføj alle, der skal med. Ingen konti, ingen adgangskoder — bare tryk på et navn. Du kan tilføje flere eller sætte PIN-koder senere.",
            })}
          </p>
        </div>

        <div className="space-y-3">
          {members.map((m, i) => (
            <div
              key={i}
              className="ff-card p-4 md:p-5"
              data-testid={`setup-row-${i}`}
            >
              <div className="flex flex-wrap gap-4 items-start">
                <div
                  className="w-14 h-14 flex items-center justify-center rounded-full text-2xl"
                  style={{
                    background: colorOf(m.color).hex,
                  }}
                >
                  {m.avatar_emoji}
                </div>
                <div className="flex-1 min-w-[180px]">
                  <label className="label-tiny block mb-1">{t({ en: "Name", da: "Navn" })}</label>
                  <input
                    type="text"
                    className="ff-input"
                    placeholder={t({ en: "e.g. Mum, Sofie, Dad", da: "fx Mor, Sofie, Far" })}
                    value={m.name}
                    onChange={(e) => update(i, { name: e.target.value })}
                    data-testid={`setup-name-${i}`}
                  />
                </div>
                {members.length > 1 && (
                  <button
                    onClick={() => removeRow(i)}
                    className="ff-btn"
                    data-testid={`setup-remove-${i}`}
                  >
                    {t({ en: "Remove", da: "Fjern" })}
                  </button>
                )}
              </div>

              <div className="mt-3">
                <label className="label-tiny block mb-2">{t({ en: "Avatar", da: "Avatar" })}</label>
                <div className="flex flex-wrap gap-1">
                  {AVATARS.map((a) => (
                    <button
                      key={a}
                      onClick={() => update(i, { avatar_emoji: a })}
                      className={`w-9 h-9 text-lg rounded-full flex items-center justify-center transition-colors ${
                        m.avatar_emoji === a
                          ? "bg-[color:var(--ink)] text-[color:var(--bg)]"
                          : "hover:bg-[color:var(--bg-soft)]"
                      }`}
                      data-testid={`setup-avatar-${i}-${a}`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <label className="label-tiny whitespace-nowrap">
                    {t({ en: "Or type your own", da: "Eller skriv din egen" })}
                  </label>
                  <input
                    type="text"
                    maxLength={8}
                    value={m.avatar_emoji}
                    onChange={(e) => {
                      const v = Array.from(e.target.value.trim())[0] || "";
                      update(i, { avatar_emoji: v || (ROLE_DEFAULT_EMOJI[m.role] || "🙂") });
                    }}
                    placeholder="😺"
                    className="ff-input max-w-[80px] text-center text-xl"
                    data-testid={`setup-custom-emoji-${i}`}
                  />
                </div>
              </div>

              <div className="mt-3">
                <label className="label-tiny block mb-2">{t({ en: "Color", da: "Farve" })}</label>
                <div className="flex gap-1.5 flex-wrap">
                  {MEMBER_COLORS.map((c) => (
                    <button
                      key={c.key}
                      onClick={() => update(i, { color: c.key })}
                      className={`w-7 h-7 rounded-full transition-transform border border-[color:var(--line)] ${
                        m.color === c.key
                          ? "ring-2 ring-[color:var(--ink)] ring-offset-2"
                          : ""
                      }`}
                      style={{ background: c.hex }}
                      data-testid={`setup-color-${i}-${c.key}`}
                      aria-label={c.label}
                      title={c.label}
                    />
                  ))}
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-3 items-end">
                <div>
                  <label className="label-tiny block mb-1">{t({ en: "Role", da: "Rolle" })}</label>
                  <div className="flex gap-1 flex-wrap">
                    <button
                      onClick={() => update(i, { role: "parent" })}
                      className={`ff-chip ${m.role === "parent" ? "is-active" : ""}`}
                      data-testid={`setup-role-parent-${i}`}
                    >
                      {t({ en: "Parent", da: "Forælder" })}
                    </button>
                    <button
                      onClick={() => update(i, { role: "kid" })}
                      className={`ff-chip ${m.role === "kid" ? "is-active" : ""}`}
                      data-testid={`setup-role-kid-${i}`}
                    >
                      {t({ en: "Kid", da: "Barn" })}
                    </button>
                    <button
                      onClick={() => update(i, { role: "pet" })}
                      className={`ff-chip ${m.role === "pet" ? "is-active" : ""}`}
                      data-testid={`setup-role-pet-${i}`}
                    >
                      {t({ en: "Pet", da: "Kæledyr" })}
                    </button>
                    <button
                      onClick={() => update(i, { role: "grandparent" })}
                      className={`ff-chip ${m.role === "grandparent" ? "is-active" : ""}`}
                      data-testid={`setup-role-grandparent-${i}`}
                    >
                      {t({ en: "Grandparent", da: "Bedsteforælder" })}
                    </button>
                    <button
                      onClick={() => update(i, { role: "other" })}
                      className={`ff-chip ${m.role === "other" ? "is-active" : ""}`}
                      data-testid={`setup-role-other-${i}`}
                    >
                      {t({ en: "Other", da: "Andet" })}
                    </button>
                  </div>
                </div>
                <div className="flex-1 min-w-[160px]">
                  <label className="label-tiny block mb-1">
                    {t({ en: "Birthday", da: "Fødselsdag" })} {m.role === "pet" ? (
                      <span className="text-[color:var(--ink-3)]">{t({ en: "(optional)", da: "(valgfrit)" })}</span>
                    ) : (
                      <span className="text-[color:var(--rough-strong)]">*</span>
                    )}
                  </label>
                  <input
                    type="date"
                    value={m.birthday}
                    onChange={(e) => update(i, { birthday: e.target.value })}
                    className="ff-input"
                    required={m.role !== "pet"}
                    data-testid={`setup-birthday-${i}`}
                  />
                  {m.role !== "pet" && (
                    <div className="label-tiny text-[color:var(--ink-3)] mt-1">
                      {t({
                        en: "Required — used for the birthday card on the calendar.",
                        da: "Påkrævet — bruges til fødselsdagskortet på kalenderen.",
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mt-6">
          <button
            onClick={addRow}
            className="ff-btn"
            data-testid="setup-add-member"
            disabled={members.length >= 15}
          >
            {t({ en: "+ Add another", da: "+ Tilføj en til" })}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="ff-btn ff-btn-primary disabled:opacity-60"
            data-testid="setup-save"
          >
            {saving
              ? t({ en: "Saving…", da: "Gemmer…" })
              : t({ en: "Let’s go →", da: "Kom i gang →" })}
          </button>
        </div>
      </div>
    </div>
  );
}
